<template>
  <section class="folio" ref="root" aria-label="Nuestro portafolio">
    <!-- Encabezado -->
    <header class="folio__head" ref="headRef">
      <h2 class="folio__eyebrow">Portafolio</h2>
      <h3 class="folio__title">
        Resultados reales con <span class="mark">negocios como el tuyo</span>
      </h3>
      <p class="folio__sub">
        Te mostramos parte de nuestro trabajo en negocios: sitios que convierten, asistentes con IA que responden 24/7
        y campañas que traen clientes. Hacemos lo mismo para ti.
      </p>

      <!-- Filtros (solo Negocios) -->
      <div class="folio__filters" ref="filtersRef">
        <button
          v-for="c in categoriesOnly"
          :key="c"
          class="chip"
          :class="{ 'chip--active': c === activeCategory }"
          @click="activeCategory = c"
        >{{ c }}</button>
      </div>
    </header>

    <!-- Grid de proyectos -->
    <div class="folio__grid" ref="gridRef">
      <article
        v-for="(p, i) in filteredProjects"
        :key="p.id || i"
        class="card"
        @mouseenter="hovered = i"
        @mouseleave="hovered = null"
        ref="cardsRef"
      >
        <div class="card__media">
          <img
            class="card__img"
            :src="p.thumb"
            :alt="p.title"
            loading="lazy"
            decoding="async"
          />
          <div class="card__badges">
            <span v-for="(t, ti) in p.tags" :key="ti" class="badge">{{ t }}</span>
          </div>
        </div>

        <div class="card__body">
          <h4 class="card__title">{{ p.title }}</h4>
          <p class="card__desc">{{ p.summary }}</p>

          <div class="card__ctas">
            <a :href="p.href" target="_blank" rel="noopener" class="btn btn--ghost">Ver proyecto</a>
          </div>
        </div>
      </article>
    </div>

    <!-- Cierre persuasivo -->
    <footer class="folio__foot" ref="footRef">
      <p class="foot__line">
        ¿Listo para un sitio y un marketing que <strong>sí venden</strong>? Agenda una demo.
      </p>
      <div class="foot__ctas">
        <a :href="demoHref" target="_blank" rel="noopener" class="btn btn--primary">Agendar demo</a>
        <a :href="waHref"  target="_blank" rel="noopener" class="btn btn--ghost">Escríbenos por WhatsApp</a>
      </div>
    </footer>
  </section>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
gsap.registerPlugin(ScrollTrigger)

export default {
  name: 'PortfolioShowcase',
  props: {
    projects: {
      type: Array,
      default: () => ([
        // ORDEN SOLICITADO: 1) EA Magic, 2) My Evento Digital, 3) Miss Coquette
        {
          id: 1,
          title: 'EA Magic – Servicios de Limpieza',
          summary: 'Landing clara y directa para convertir llamadas y mensajes.',
          thumb: new URL('@/assets/port3.jpg', import.meta.url).href,
          href:  'https://ea-magic.com',
          tags: ['Landing', 'Negocios', 'Contact'],
          category: 'Negocios'
        },
        {
          id: 2,
          title: 'My Evento Digital',
          summary: 'Página y Redes Sociales con IA (contestador automático).',
          thumb: new URL('@/assets/myevento.png', import.meta.url).href,
          href:  'https://myevento-digital.com',
          tags: ['IA', 'Social', 'Embudo'],
          category: 'Negocios'
        },
        {
          id: 3,
          title: 'Miss Coquette – Rentas',
          summary: 'Catálogo y reservas con generación de leads.',
          thumb: new URL('@/assets/port1.jpg', import.meta.url).href,
          href:  'https://misscoquette.com.mx',
          tags: ['Landing', 'Leads', 'Google Ads'],
          category: 'Negocios'
        }
      ])
    },
    categories: {
      type: Array,
      default: () => ['Negocios']
    },
    demoHref: { type: String, default: 'https://calendly.com/' },
    waPhone:  { type: String, default: '+16126005144' },
    waText:   { type: String, default: 'Hola, vi su portafolio y quiero una cotización.' }
  },
  setup (props) {
    const root = ref(null)
    const headRef = ref(null)
    const filtersRef = ref(null)
    const gridRef = ref(null)
    const footRef = ref(null)
    const cardsRef = ref([])
    const hovered = ref(null)
    const activeCategory = ref('Negocios')

    const categoriesOnly = computed(() => props.categories) // ['Negocios']
    const filteredProjects = computed(() =>
      props.projects.filter(p => p.category === activeCategory.value)
    )

    const waHref = computed(() => {
      const num = props.waPhone.replace(/\D/g, '')
      return `https://wa.me/${num}?text=${encodeURIComponent(props.waText)}`
    })
    const waHrefFor = (p) => {
      const num = props.waPhone.replace(/\D/g, '')
      const text = `Hola, me interesa el proyecto: ${p.title}. ¿Podemos cotizar?`
      return `https://wa.me/${num}?text=${encodeURIComponent(text)}`
    }

    let ctx
    onMounted(() => {
      ctx = gsap.context(() => {
        gsap.set([headRef.value, filtersRef.value], { autoAlpha: 0, y: 18 })
        gsap.set(footRef.value, { autoAlpha: 0, y: 18 })
        gsap.set(gridRef.value, { autoAlpha: 0, y: 18 })

        gsap.timeline({
          scrollTrigger: { trigger: root.value, start: 'top 80%', once: true }
        })
        .to(headRef.value, { autoAlpha: 1, y: 0, duration: 0.5, ease: 'power2.out' })
        .to(filtersRef.value, { autoAlpha: 1, y: 0, duration: 0.45, ease: 'power2.out' }, '-=0.2')
        .to(gridRef.value, { autoAlpha: 1, y: 0, duration: 0.5, ease: 'power3.out' }, '-=0.05')

        const cards = gridRef.value.querySelectorAll('.card')
        gsap.from(cards, {
          y: 20, autoAlpha: 0, duration: 0.35, stagger: 0.06, ease: 'power2.out',
          scrollTrigger: { trigger: gridRef.value, start: 'top 85%', once: true }
        })

        gsap.to(footRef.value, {
          autoAlpha: 1, y: 0, duration: 0.5, ease: 'power2.out',
          scrollTrigger: { trigger: footRef.value, start: 'top 85%', once: true }
        })
      }, root.value)
    })

    onBeforeUnmount(() => { ctx && ctx.revert() })

    return {
      root, headRef, filtersRef, gridRef, footRef, cardsRef,
      hovered, activeCategory, categoriesOnly, filteredProjects,
      waHref, waHrefFor
    }
  }
}
</script>

<style scoped>
/* Tema claro (fondo blanco) */
.folio{
  background:#ffffff; color:#111827; padding:48px 0 56px;
  font-family:'Poppins',system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial;
}
.folio__head{ max-width:1100px; margin:0 auto; padding:0 24px; text-align:center; }
.folio__eyebrow{
  margin:0 0 6px; color:#6b7280; font-weight:800; font-size:13px; letter-spacing:.6px; text-transform:uppercase;
}
.folio__title{
  margin:0 0 10px; font-size:clamp(28px,4.6vw,42px); line-height:1.15; font-weight:900; color:#0f172a;
}
.mark{ background:#6f2cff; color:#fff; padding:2px 8px; border-radius:8px; }
.folio__sub{ margin:0 auto 14px; max-width:820px; color:#374151; font-size:16px; }

/* Filtros (solo Negocios) */
.folio__filters{ display:flex; flex-wrap:wrap; gap:10px; justify-content:center; margin:14px 0 0; }
.chip{
  appearance:none; border:1px solid #e5e7eb; background:#f9fafb; color:#111827;
  padding:8px 12px; border-radius:999px; font-weight:700; font-size:12px; cursor:pointer;
}
.chip--active{ background:#6f2cff; color:#fff; border-color:#6f2cff; }

/* Grid */
.folio__grid{
  max-width:1200px; margin:22px auto 0; padding:0 24px;
  display:grid; gap:18px; grid-template-columns:repeat(3,1fr);
}
@media (max-width:1024px){ .folio__grid{ grid-template-columns:repeat(2,1fr); } }
@media (max-width:640px){ .folio__grid{ grid-template-columns:1fr; } }

/* Card (tema claro) */
.card{
  position:relative; background:#ffffff; border:1px solid #e5e7eb; border-radius:16px; overflow:hidden;
  display:flex; flex-direction:column; min-height:100%;
  transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease;
  box-shadow: 0 2px 8px rgba(17,24,39,.04);
}
.card:hover{ transform: translateY(-4px); border-color:#d1d5db; box-shadow:0 12px 28px rgba(17,24,39,.12); }

.card__media{ position:relative; aspect-ratio: 16/9; overflow:hidden; background:#f3f4f6; }
.card__img{
  width:100%; height:100%; object-fit:cover; display:block; transform:scale(1);
  transition: transform .5s ease;
}
.card:hover .card__img{ transform:scale(1.06); }
.card__badges{
  position:absolute; left:10px; bottom:10px; display:flex; gap:6px; flex-wrap:wrap;
}
.badge{
  background:#ffffff; color:#111827; border:1px solid #e5e7eb; padding:4px 8px; border-radius:999px;
  font-weight:700; font-size:11px; box-shadow:0 1px 3px rgba(0,0,0,.06);
}

.card__body{ padding:14px 14px 16px; display:flex; flex-direction:column; gap:8px; }
.card__title{ margin:0; font-size:18px; font-weight:900; color:#0f172a; }
.card__desc{ margin:0; color:#374151; font-size:14px; }

/* Botones */
.card__ctas{ margin-top:auto; display:flex; gap:10px; flex-wrap:wrap; }
.btn{
  appearance:none; border:0; border-radius:10px; padding:10px 14px; font-weight:800; font-size:13px; cursor:pointer; text-decoration:none;
}
.btn--primary{ background:linear-gradient(180deg,#7a2bff,#5720c9); color:#fff; box-shadow:0 10px 18px rgba(87,32,201,.25); }
.btn--ghost{ background:#ffffff; color:#111827; border:1px solid #e5e7eb; }

/* Footer */
.folio__foot{ max-width:900px; margin:24px auto 0; padding:0 24px; text-align:center; color:#374151; }
.foot__line{ margin:0 0 10px; font-size:16px; }
.foot__ctas{ display:flex; gap:10px; flex-wrap:wrap; justify-content:center; }
</style>
